<template>
    <div class="gallery-handle">
        <div class="mobile-wrapper">
            <div v-if="myOpenSelectable" class="mobile-gallery-handle">
                <div class="buttons">
                    <reo-common-button
                        class="mobile-download-button"
                        @click="downloadEvents"
                        :disabled="maxLength === 0"
                        colorType="light"
                        size="middle"
                        svgIcon
                    >
                        <template #svgIcon>
                            <download-icon />
                        </template>
                        <template #label>
                            {{ downloadText }}
                        </template>
                    </reo-common-button>
                    <reo-common-button
                        class="mobile-delete-button"
                        colorType="red"
                        size="middle"
                        svgIcon
                        :disabled="maxLength === 0"
                        :label="$t('video.del')"
                        @click="deleting = true"
                    >
                        <template #svgIcon>
                            <delete-icon/>
                        </template>
                    </reo-common-button>
                </div>
            </div>
        </div>
        <div class="cloud-handle">
            <div
                v-if="myOpenSelectable"
                class="cloud-handle-bounce-patcher"
            ></div>
            <div class="cloud-handle-inner"
                :class="{'fixed':myOpenSelectable}">
                <div class="left">
                    <button
                        class="selectable-button"
                        :class="myOpenSelectable ? 'selectable-close-button' : 'selectable-open-button'"
                        type="button"
                        :aria-label="myOpenSelectable ? $t('cloud.selectedLabel') : $t('cloud.select')"
                        @click="myOpenSelectable = !myOpenSelectable"
                    >
                        <img v-if="myOpenSelectable" :src="InputCloseIcon" alt="" />
                        <template v-else>{{ $t('cloud.select') }}</template>
                    </button>
                    <reo-old-checkbox
                        v-model="mySelectedAll"
                        :label="$tc('selectAll')"
                        type="fill"
                        v-show="myOpenSelectable"
                        color="primary"
                        :indeterminate="indeterminate"
                        size="small"
                        @input="selectAll"
                    />
                    <reo-common-button
                        @click="downloadEvents"
                        :disabled="maxLength === 0"
                        v-show="myOpenSelectable"
                        colorType="light"
                        size="middle"
                        svgIcon
                    >
                        <template #svgIcon>
                            <download-icon />
                        </template>
                        <template #label>
                            {{ downloadText }}
                        </template>
                    </reo-common-button>
                    <reo-common-button
                        v-show="myOpenSelectable"
                        colorType="red"
                        size="middle"
                        svgIcon
                        :disabled="maxLength === 0"
                        :label="$t('video.del')"
                        @click="deleting = true"
                    >
                        <template #svgIcon>
                            <delete-icon/>
                        </template>
                    </reo-common-button>
                </div>
                <reo-old-checkbox
                    v-if='showWillExpiredGallerySwitch'
                    class="expired-checkbox"
                    v-model="myShowExpired"
                    type="fill"
                    size="small"
                    color="primary"
                    :label="$t('cloud.showExpired')"
                    @input="myShowExpiredHandle"
                ></reo-old-checkbox>
            </div>
        </div>
        <reo-dialog
            v-if="deleting"
            :title="$tc( 'dialog.del' )"
            :labelOfConfirm="$t( 'dialog.yes' )"
            :labelOfCancel="$t( 'dialog.no' )"
            :switchButton="true"
            @confirm="deleteGalleryItem(true)"
            @cancel="deleteGalleryItem(false)"
        ></reo-dialog>
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ReoOldCheckbox from '@/basic/Checkbox.vue';
import ReoDialog from '@/basic/Dialog.vue';
import { unfoldError, unfoldSuc } from '@/common/js/notice';
import { debounce, splitArrayIntoChunks } from '@/common/js/utils';
import { deleteEvents, getEventList, MAX_EVENT_COUNT } from '@/api/event';
import { IEventWithExtraInfo } from '../lib';
import { MultiFileDownloader, TDownloadEntryMap } from '@/utils/download';
import { generateDownloadMapFromEvents, getDownloadText, getFileNameFromTime } from '@/utils/business';
import InputCloseIcon from '@/common/images/common/combo/co_inputclose_full.svg';

const DEBOUNCE_DELAY = 300;

export default Vue.extend({

    props: {

        selectable: Boolean,
        selectedAll: Boolean,
        galleryList: Array as PropType<IEventWithExtraInfo[]>,
        showExpired: Boolean,
        /** 是否显示三天内过期的checkbox */
        showWillExpiredGallerySwitch: Boolean

    },

    components: {

        ReoOldCheckbox,
        ReoDialog,

    },
    data () {

        return {

            mySelectedAll: this.selectedAll,
            myOpenSelectable: this.selectable,
            deleting: false,
            myShowExpired: this.showExpired,
            InputCloseIcon

        };

    },
    mounted () {

        if ( document.body.clientWidth < 1200 ) {

            $( 'footer.pc-body' ).toggleClass( 'has-handle', this.myOpenSelectable );

        }

    },
    destroyed () {

        $( 'footer.pc-body' ).removeClass( 'has-handle' );

    },
    methods: {

        deleteGalleryItem: debounce( async function( this: any, goDelete: boolean ) {

            if ( goDelete ) {

                let eventIDs: string[] = [];
                let galleryList = this.galleryList;
                for ( let galleryItem of galleryList ) {

                    if ( galleryItem.checked ) {

                        eventIDs.push( galleryItem.id );

                    }

                }

                try {

                    await deleteEvents( eventIDs );

                    this.$emit( 'delete', eventIDs );

                    unfoldSuc( 'suc.delete_video', 5000 );

                } catch ( error ) {

                    unfoldError( 'errors.delete_video', 5000 );
                    console.error( error );

                }

            }
            this.deleting = false;

        }, DEBOUNCE_DELAY ),
        async getDownloadVideoUrls( eventIds: string[] ) {

            const { items: eventList } = await getEventList({ idList: eventIds });
            
            return generateDownloadMapFromEvents( eventList );
        
        },

        async downloadEvents () {

            const selectedEvents = this.galleryList.filter( event => event.checked );
            const eventIdChunks = splitArrayIntoChunks( selectedEvents.map( event => event.id ), MAX_EVENT_COUNT );
            let chunkIndex = 0;
            let currentEventIds = eventIdChunks[chunkIndex];
            const getCurrentUrls = (): Promise<TDownloadEntryMap> => this.getDownloadVideoUrls( currentEventIds );
            const downloader = new MultiFileDownloader( await getCurrentUrls(), getCurrentUrls );
            const sortedEvents = selectedEvents.sort(( a, b ) => a.recordedAt - b.recordedAt );
            const getNameFromLength = ( length: number ): string => {

                if ( length === 0 ) {

                    return '';
                
                }
                if ( length === 1 ) {

                    return getFileNameFromTime( sortedEvents[0].recordedAt );
                
                }
                return `${getFileNameFromTime( sortedEvents[0].recordedAt )}_${getFileNameFromTime( sortedEvents[length - 1].recordedAt )}`;
            
            };
            const fileName = getNameFromLength( sortedEvents.length );
            if ( eventIdChunks.length === 1 ) {

                await downloader.download( fileName );
                return;

            }
            await downloader.downloadBatches( fileName, async () => {

                currentEventIds = eventIdChunks[++chunkIndex];
                return currentEventIds ? getCurrentUrls() : undefined;

            });

        },

        selectAll ( checked: boolean ): void {

            this.$emit( 'selectedAllChange', checked );

        },

        myShowExpiredHandle ( checked: boolean ): void {

            this.$emit( 'showExpiredChange', checked );

        }

    },

    watch: {

        myOpenSelectable ( myOpenSelectable ) {

            this.$emit( 'openSelectableChange', myOpenSelectable );
            if ( document.body.clientWidth < 1200 ) {

                $( 'footer.pc-body' ).toggleClass( 'has-handle', myOpenSelectable );

            }

        },
        selectedAll ( selectedAll ) {

            this.mySelectedAll = selectedAll;

        },
        selectable ( selectable ) {

            this.myOpenSelectable = selectable;

        },
        showExpired ( showExpired ) {

            this.myShowExpired = showExpired;

        }

    },
    computed: {

        maxLength () {

            let maxLength = 0;
            let galleryList: any[] = this.galleryList;
            for ( let galleryItem of galleryList ) {

                if ( galleryItem.checked ) {

                    maxLength += 1;

                }
                if ( maxLength === 2 ) {

                    return maxLength;

                }

            }
            return maxLength;

        },

        indeterminate(): boolean {

            let galleryList = this.galleryList;
            let selectedCount = 0;
            let selectableCount = 0;

            for ( let galleryItem of galleryList ) {

                if ( galleryItem.checked ) {

                    selectedCount++;

                }

            }
            return selectedCount > 0 && selectedCount < selectableCount;

        },

        userId(): string {

            return this.$store.getters.userId;

        },

        lang(): string  {

            return this.$store.getters.lang;

        },
        
        downloadText(): string {

            return getDownloadText( this.$i18n, this.galleryList.filter( event => event.checked ));

        }

    }

});
</script>

<style lang="less" scoped>
    .gallery-handle{
        position: relative;
        background-color: @bg_01;
    }
    .mobile-wrapper{
        display: none;
    }
    .gallery-handle /deep/ .reo-button-inner.middle{
        border-radius: 8px;
        box-sizing: border-box;
        font-family: Poppins, sans-serif;
        font-size: 16px;
        height: 48px;
        letter-spacing: -0.32px;
        line-height: 22px;
        min-width: 120px;
        padding: 0 24px;

        .button-svg-icon svg{
            height: 22px;
            width: 22px;
        }

        &.light{
            border-color: #0050e2;
            color: #0050e2;
        }
        &.red{
            border-color: #ff4747;
            color: #ff4747;
        }
        &.light:hover:not(:disabled){
            background: #0050e2;
            color: #fff;
        }
        &.red:hover:not(:disabled){
            background: #ff4747;
            color: #fff;
        }
        .button-svg-icon{
            color: inherit;
            fill: currentColor;
        }
        &:disabled{
            background: transparent;
            cursor: not-allowed;
            @media @pc{
                opacity: 0.4;
            }
        }
    }

    .cloud-handle{
        max-width: 1200px;
        display: flex;
        margin: 0 auto;
        .cloud-handle-bounce-patcher{
            height: 73px;
        }
        .cloud-handle-inner{
            /** 不想改动dom结构，简单处理一下阴影问题 */
            box-shadow: 20px 0 var(--bg_01), -20px 0 var(--bg_01), 0 -2px var(--bg_01);
            background-color: @bg_01;
            top: 194px;
            width: 1200px;
            color: @brand_light;
            text-align: left;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 24px;
            padding-top: 1px;
            &.fixed{
                position: fixed;
                z-index: 4;
            }
            .left{
                display: flex;
                align-items: center;
                gap: 20px;
            }
        }
    }
    .cloud-handle-inner .checkbox-wrap-container .text{
        font-weight: 500;
    }
    .selectable-button{
        padding: 0;
        border: 0;
        background: transparent;
        cursor: pointer;
    }
    .selectable-open-button{
        color: @brand_light;
        font-size: 14px;
        font-weight: 500;
        line-height: 21px;
    }
    .selectable-close-button{
        width: 32px;
        height: 32px;
        img{
            display: block;
            width: 100%;
            height: 100%;
        }
    }
    .app-platform .cloud-handle-inner.fixed{
        top: 114px;
    }
    @media @mobile {
        .gallery-handle{
            background: @bg_01;
            height: 44px;
        }
        .cloud-handle{
            display: none;
        }
        .mobile-wrapper{
            display: block;
            color: @font_primary;
            font-size: 14px;
            font-weight: 400;
        }
        .mobile-gallery-handle{
            background: #fff;
            bottom: 0;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            gap: 16px;
            justify-content: center;
            padding: 16px 20px;
            position: fixed;
            width: 100%;
            z-index: 10;
        }
        .buttons{
            align-items: center;
            display: flex;
            gap: 10px;
            width: 100%;
            /deep/ .button-container{
                flex: 1 1 0;
                min-width: 96px;
            }
            /deep/ .reo-button-inner.middle{
                border-radius: 8px;
                font-size: 14px;
                height: 36px;
                letter-spacing: normal;
                line-height: 20px;
                min-width: 0;
                padding: 7px 12px;
            }
            /deep/ .reo-button-inner.middle .button-svg-icon svg{
                height: 18px;
                width: 18px;
            }
        }
        .mobile-download-button /deep/ .reo-button-inner.disabled{
            border-color: rgba(51, 51, 51, 0.4);
            color: rgba(51, 51, 51, 0.4);
        }
        .mobile-delete-button /deep/ .reo-button-inner.red{
            border-color: #ff4747;
            color: #ff4747;

            &.disabled{
                border-color: rgba(213, 73, 65, 0.3);
                color: rgba(213, 73, 65, 0.3);
                .button-svg-icon{
                    color: rgba(213, 73, 65, 0.3);
                    fill: rgba(213, 73, 65, 0.3);
                }
            }
        }
    }
</style>
